import { LinkOutlined, LoadingOutlined, PictureOutlined } from '@ant-design/icons';
import { Button, message, Modal, Tabs } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { getAiRuntime } from '../runtime';
import { _chatShareCreate } from '../services/aiAssistant';
import { isOidcRequiredError } from '../utils/aiAssistantOidc';
import { ChatMsg } from './messageMapper';
import ShareImageCanvas from './ShareImageCanvas';
interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  selectedMessageIds: string[];
  selectedMessages: ChatMsg[];
}

const buildShareUrl = (shareId: string): string => {
  // appBase 由各应用注入（/abtest、/widget、/smart …）
  return `${window.location.origin}${getAiRuntime().appBase}/ai-assistant/share?shareId=${shareId}`;
};

async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fallback
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

async function copyPngBlob(dataUrl: string): Promise<boolean> {
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) return false;
  try {
    const blob = await fetch(dataUrl).then((r) => r.blob());
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    return true;
  } catch {
    return false;
  }
}

function triggerDownload(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function ShareDialog({ open, onClose, projectId, selectedMessageIds, selectedMessages }: Props) {
  const [activeTab, setActiveTab] = useState<'link' | 'image'>('link');
  const [linkLoading, setLinkLoading] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState<string>('');
  const [imageRendering, setImageRendering] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [copyImageLoading, setCopyImageLoading] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const renderingRef = useRef(false);

  // 关闭时重置 image 状态（保留 activeTab）
  useEffect(() => {
    if (!open) {
      setImageDataUrl('');
      setImageError(false);
      setImageRendering(false);
      setCopyImageLoading(false);
      setLinkLoading(false);
      renderingRef.current = false;
    }
  }, [open]);

  // 切到 image tab 时按需渲染
  useEffect(() => {
    if (!open || activeTab !== 'image') return;
    if (imageDataUrl || renderingRef.current) return;
    renderingRef.current = true;
    setImageRendering(true);
    setImageError(false);
    (async () => {
      try {
        const mod = await import('html-to-image');
        // 等下一帧确保 ShareImageCanvas DOM 已挂载
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });
        const node = canvasRef.current;
        if (!node) throw new Error('canvas node missing');
        // 兜底：若 toPng 长时间不 resolve（如外链图片卡死），10s 抛错回退
        const timeout = new Promise<string>((_, reject) => {
          setTimeout(() => reject(new Error('toPng timeout')), 10000);
        });
        const url = await Promise.race([
          mod.toPng(node, {
            pixelRatio: 2,
            cacheBust: true,
            backgroundColor: '#ffffff',
            skipFonts: true
          }),
          timeout
        ]);
        setImageDataUrl(url);
      } catch {
        setImageError(true);
      } finally {
        renderingRef.current = false;
        setImageRendering(false);
      }
    })();
  }, [open, activeTab, imageDataUrl]);

  const handleCopyLink = async () => {
    if (linkLoading) return;
    if (!projectId || selectedMessageIds.length === 0) {
      message.warning('请先选择需要分享的对话');
      return;
    }
    setLinkLoading(true);
    try {
      const res = await _chatShareCreate({ projectId, messageIds: selectedMessageIds });
      const shareId = res?.shareId;
      if (!shareId) {
        // 接口业务码成功但缺字段，自定义文案；规约例外，注释说明
        message.error('生成分享链接失败');
        return;
      }
      const url = buildShareUrl(shareId);
      const ok = await copyText(url);
      if (ok) {
        message.success('链接已复制');
      } else {
        // 复制为浏览器原生 API，非接口调用，规约例外
        message.error('复制失败');
      }
    } catch (err) {
      if (isOidcRequiredError(err)) {
        // OIDC 错误已由调用方/拦截器处理跳转，这里不弹 toast
        return;
      }
      // 错误提示由 request 拦截器统一处理
    } finally {
      setLinkLoading(false);
    }
  };

  const handleCopyImage = async () => {
    if (!imageDataUrl || copyImageLoading) return;
    setCopyImageLoading(true);
    try {
      const ok = await copyPngBlob(imageDataUrl);
      if (ok) {
        message.success('图片已复制');
      } else {
        triggerDownload(imageDataUrl, `share-${Date.now()}.png`);
        message.success('已下载图片，请手动分享');
      }
    } finally {
      setCopyImageLoading(false);
    }
  };

  return (
    <>
      <Modal
        title="分享对话"
        open={open}
        onCancel={onClose}
        footer={null}
        width={560}
        destroyOnClose={false}
        maskClosable
        zIndex={1200}
      >
        {/* off-screen 渲染源：弹窗一打开就 mount，避免切 tab 时机问题 */}
        {open && (
          <div
            style={{
              position: 'fixed',
              left: -99999,
              top: 0,
              pointerEvents: 'none',
              opacity: 0,
              zIndex: -1
            }}
            aria-hidden
          >
            <ShareImageCanvas ref={canvasRef} messages={selectedMessages} />
          </div>
        )}
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as 'link' | 'image')}
          items={[
            {
              key: 'link',
              label: (
                <span>
                  <LinkOutlined /> 分享链接
                </span>
              ),
              children: (
                <div>
                  <div
                    style={{
                      padding: '14px 16px',
                      background: '#F7F8FA',
                      borderRadius: 8,
                      color: '#4B5563',
                      fontSize: 14
                    }}
                  >
                    点击下方「复制链接」按钮生成分享链接
                  </div>
                  <div style={{ marginTop: 12, color: '#8C8C8C', fontSize: 12 }}>
                    Agent 平台内有权限的成员打开此链接即可查看分享的对话内容
                  </div>
                  <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={onClose}>取 消</Button>
                    <Button type="primary" icon={<LinkOutlined />} loading={linkLoading} onClick={handleCopyLink}>
                      复制链接
                    </Button>
                  </div>
                </div>
              )
            },
            {
              key: 'image',
              label: (
                <span>
                  <PictureOutlined /> 生成图片
                </span>
              ),
              children: (
                <div>
                  <div
                    style={{
                      maxHeight: 380,
                      overflow: 'auto',
                      background: '#F7F8FA',
                      borderRadius: 8,
                      padding: 12,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: imageRendering || imageError ? 'center' : 'flex-start',
                      minHeight: 160
                    }}
                  >
                    {imageRendering ? (
                      <div style={{ color: '#8C8C8C', fontSize: 13, padding: 24 }}>
                        <LoadingOutlined style={{ marginRight: 6 }} />
                        正在生成图片...
                      </div>
                    ) : imageError ? (
                      <div style={{ color: '#FF525D', fontSize: 13, padding: 24 }}>图片生成失败</div>
                    ) : imageDataUrl ? (
                      <img
                        src={imageDataUrl}
                        alt="share preview"
                        style={{ maxWidth: '100%', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                      />
                    ) : null}
                  </div>
                  <div style={{ marginTop: 12, color: '#8C8C8C', fontSize: 12 }}>
                    以上即最终生成效果，点击「复制图片」即可把高清 PNG 写入剪贴板（若浏览器不支持将自动下载）
                  </div>
                  <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={onClose}>取 消</Button>
                    <Button
                      type="primary"
                      icon={<PictureOutlined />}
                      loading={copyImageLoading}
                      disabled={!imageDataUrl}
                      onClick={handleCopyImage}
                    >
                      复制图片
                    </Button>
                  </div>
                </div>
              )
            }
          ]}
        />
      </Modal>
    </>
  );
}
